import ContentBody from "../common/content-body/ContentBody";
import MsFilter from "./ms-filter/MsFilter";
import HolidayTable from "./holiday-table/HolidayTable";
import MsTable from "./ms-table/MsTable";
import { useRecoilValue } from "recoil";
import { holidaysState, mobilityStoresState } from "./state";
import { useContext, useEffect, useState, useMemo } from "react"; // <--- MODIFIED
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { associateHolidays } from "./services/http.services";
import Dialog from "@mui/material/Dialog";
import DialogCloseButton from "../../booking/components/DialogCloseButton";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../util/state/globalAction";
import { snackbarTypes } from "../../../util/state/globalVariables";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CustomLoader from "../../../util/component/CustomLoader";

function MsHoliday() {
  // We need the 'i18n' object for date formatting
  const { t, i18n } = useTranslation(["network", "translation"]); // <--- MODIFIED
  const { t: t2 } = useTranslation(["translation"]);
  const { dispatchGlobalData } = useContext(GlobalContext);

  const holidays = useRecoilValue(holidaysState);
  const mobilityStores = useRecoilValue(mobilityStoresState);

  const [showAssociateConfirmation, setShowAssociateConfirmation] =
    useState(false);

  // --- Calculate selected items with useMemo ---
  const selectedHolidays = useMemo(
    () => holidays.filter((value) => value._select),
    [holidays]
  );

  const selectedMobilityStores = useMemo(
    () => mobilityStores.filter((value) => value._select),
    [mobilityStores]
  );

  const associateHolidaysMutation = useMutation(associateHolidays);

  function handleAssociate() {
    associateHolidaysMutation.mutateAsync(
      {
        holidayList: selectedHolidays, // <--- MODIFIED (use memoized list)
        mobilityStoreList: selectedMobilityStores, // <--- MODIFIED (use memoized list)
      },
      {
        onSuccess(params) {
          setShowAssociateConfirmation(false);

          if (params.code === "VALIDATION_ERROR") {
            dispatchGlobalData({
              type: setSnackbar,
              payload: {
                snackbar: {
                  message: t2("error.holidayMsMismatch"), // "Holidays and Mobility Stores tenants do not match"
                  type: snackbarTypes.error,
                  show: true,
                  autoClose: true,
                },
              },
            });
          } else {
            dispatchGlobalData({
              type: setSnackbar,
              payload: {
                snackbar: {
                  message: t2("success.completed"),
                  type: snackbarTypes.success,
                  show: true,
                  autoClose: true,
                },
              },
            });
          }
        },
        onError() {
          dispatchGlobalData({
            type: setSnackbar,
            payload: {
              snackbar: {
                message: t2("error.tryLater"),
                type: snackbarTypes.error,
                show: true,
                autoClose: true,
              },
            },
          });
        },
      }
    );
  }

  // Options for formatting the date in the recap list
  const recapDateOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  return (
    <>
      <ContentBody>
        <MsFilter />
        <MsTable />
        <HolidayTable />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 3,
            position: "sticky",
            bottom: 16,
            zIndex: 10,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{
              boxShadow: 4,
              px: 4,
              borderRadius: 2,
            }}
            onClick={() => setShowAssociateConfirmation(true)}
            disabled={
              selectedHolidays.length === 0 ||
              selectedMobilityStores.length === 0
            }
          >
            {t("msHoliday.labels.associateHolidays")}
          </Button>
        </Box>

        <Dialog
          open={showAssociateConfirmation}
          onClose={() => setShowAssociateConfirmation(false)}
          maxWidth="md"
          fullWidth //
        >
          <DialogCloseButton
            onClick={() => setShowAssociateConfirmation(false)}
          />

          <DialogTitle>{t("msHoliday.labels.associateHolidays")}</DialogTitle>

          {/* --- Dialog Content with Recap --- */}
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              {t("msHoliday.messages.doYouConfirm")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mt: 2,
              }}
            >
              {/* Column 1: Mobility Stores */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  {t("msHoliday.labels.mobilityStores", "Mobility Stores")} (
                  {selectedMobilityStores.length})
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflow: "auto",
                    border: "1px solid #ddd",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {selectedMobilityStores.map((store) => (
                      <li key={store.id}>{store.name}</li>
                    ))}
                  </ul>
                </Box>
              </Box>

              {/* Column 2: Holidays */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h3" gutterBottom>
                  {t("msHoliday.labels.holidays", "Holidays")} (
                  {selectedHolidays.length})
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflow: "auto",
                    border: "1px solid #ddd",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <ul style={{ margin: 0, paddingLeft: "20px" }}>
                    {selectedHolidays.map((holiday) => (
                      <li key={holiday.name}>
                        {holiday.name}
                        {holiday._date &&
                          ` (${new Date(holiday._date).toLocaleDateString(
                            i18n.language,
                            recapDateOptions
                          )})`}
                      </li>
                    ))}
                  </ul>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          {/* --- End of Modified Section --- */}

          <DialogActions>
            <Button onClick={() => setShowAssociateConfirmation(false)}>
              {t("msHoliday.labels.cancel")}
            </Button>
            <Button onClick={handleAssociate}>
              {t("msHoliday.labels.save")}
            </Button>
          </DialogActions>
        </Dialog>
      </ContentBody>
      <CustomLoader
        loading={
          associateHolidaysMutation.isLoading
        }
      />
    </>
  );
}

export default MsHoliday;